import React from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { CloudUpload, Storage } from '@mui/icons-material';

export type ImportPhase = 'uploading' | 'processing';

interface ImportProgressDialogProps {
    open: boolean;
    /** Name of the file being imported, so the user can confirm they picked the right one. */
    fileName?: string;
    /** 0-100, bytes actually sent. Only meaningful while phase is 'uploading'. */
    uploadPct: number;
    phase: ImportPhase;
    title?: string;
}

/**
 * Blocking progress dialog for CSV imports.
 *
 * The upload half reports real byte progress. The server half cannot — the import
 * is one synchronous request that returns only after every row has been written —
 * so it shows an indeterminate bar and tells the user to sit tight rather than
 * pretending to know how far along it is.
 */
const ImportProgressDialog = ({
    open,
    fileName,
    uploadPct,
    phase,
    title = 'Importing CSV',
}: ImportProgressDialogProps) => (
    <Dialog
        open={open}
        // Deliberately not dismissible: closing this would not cancel the request,
        // it would only hide an import that is still running.
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
    >
        <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                {fileName && (
                    <Typography variant="body2" color="text.secondary" noWrap title={fileName}>
                        {fileName}
                    </Typography>
                )}

                <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        {phase === 'uploading'
                            ? <CloudUpload fontSize="small" color="primary" />
                            : <Storage fontSize="small" color="primary" />}
                        <Typography variant="body2" fontWeight={600}>
                            {phase === 'uploading' ? `Uploading… ${uploadPct}%` : 'Processing rows…'}
                        </Typography>
                    </Stack>

                    <LinearProgress
                        variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
                        value={uploadPct}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>

                <Typography variant="caption" color="text.secondary">
                    {phase === 'uploading'
                        ? 'Sending the file to the server.'
                        : 'The server is writing every row. Large files can take a few minutes — please keep this tab open.'}
                </Typography>
            </Stack>
        </DialogContent>
    </Dialog>
);

export default ImportProgressDialog;
